/**
 * Praxis Tool theme system.
 * A saved day/night preference applies only to pages with a defined light design.
 * Other screens retain dark styling; forced public palettes take precedence.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useState, useCallback } from "react";
import {automaticAppearanceAt} from './automaticAppearance';
import {useAuth} from '../../lib/auth';

import "./essence/patients.css";
import { useLocation, useNavigate } from "react-router-dom";
import {isGalleryPreview, isDaylightAppearance, hasLightDesign} from './essence/galleryPreview';
import './essence/gallery-detail.css';
import './essence/daylight.css';
import './essence/daylight-materials.css';
import './essence/night-materials.css';
import './essence/site-layout.css';

// A new account-scoped choice starts everyone on Auto. Legacy values also came
// from preview URLs and cannot establish that the user deliberately chose them.
const KEY = "praxis_tool_appearance_v2:";
const MODES = ['light','auto','dark'];

const TOKENS = {
  dark: {
    bg: "#0F1419",
    panel: "#1A1F26",
    panel2: "#252B33",
    border: "#2F3741",
    borderStrong: "#3A434E",
    text: "#E8E6E1",
    textDim: "#8B95A0",
    textMuted: "#5A6470",
    accent: "#C9A66B",
    accentLight: "#E8C988",
    accentBg: "rgba(201,166,107,0.15)",
    plant: "#7B9971",
    animal: "#C46E5C",
    mineral: "#6B8AAA",
    nosode: "#A87BA9",
    // M3f §4 — "correct" has its OWN green. The kingdom palette encodes meaning
    // (plant/animal/mineral/nosode), so the plant green must never double as a
    // verdict: the moment a kingdom colour and a progress colour share a screen
    // the same green would carry two meanings. Cooler and more saturated than
    // `plant`, so the two never get confused side by side.
    right: "#46A07E",
    overlay: "rgba(0,0,0,0.65)",
  },
  light: {
    bg: "#F8F8F5",
    panel: "#FDFDFB",
    panel2: "#EFF1EC",
    border: "#D2D4C7",
    borderStrong: "#BAAA81",
    text: "#263239",
    textDim: "#46514D",
    textMuted: "#626C65",
    accent: "#82703D",
    accentLight: "#82703D",
    accentBg: "rgba(181,154,70,0.08)",
    plant: "#6B7B61",
    animal: "#B0513A",
    mineral: "#4A6680",
    nosode: "#7B5A7C",
    right: "#2E7D5B",
    overlay: "rgba(44,53,41,0.45)",
  },
};

function applyTheme(name) {
  const tokens = TOKENS[name] || TOKENS.dark;
  const root = document.documentElement;
  Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(`--pt-${k}`, v));
  root.style.setProperty("--pt-theme", name);
  root.dataset.ptTheme = name;
  // Follow the theme on the browser chrome (mobile address/nav bar) and the
  // overscroll/rubber-band area so dark mode has no white flash.
  const bg = tokens.bg;
  root.style.backgroundColor = bg;
  if (document.body) document.body.style.backgroundColor = bg;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", bg);
}

function readSaved(owner) {
  try {
    const value=window.localStorage.getItem(KEY+owner);
    return MODES.includes(value)?value:'auto';
  } catch {
    return "auto";
  }
}

const ThemeContext = createContext({ theme: "dark", preference:'auto', lightAvailable: false, setTheme: () => {}, toggle: () => {} });

export function ThemeProvider({ children, forceTheme = null }) {
  const {user}=useAuth();
  const owner=user?.user_id||'anonymous';
  const [saved,setSaved]=useState(()=>({owner,value:readSaved(owner)}));
  const preference=saved.owner===owner?saved.value:readSaved(owner);
  useLayoutEffect(()=>setSaved({owner,value:readSaved(owner)}),[owner]);
  const [now,setNow]=useState(()=>new Date());
  const location = useLocation();
  const navigate = useNavigate();
  const {pathname} = location;
  const lightAvailable = !forceTheme && hasLightDesign(location, window.location.origin);
  const appearance = new URLSearchParams(location.search).get('appearance');
  const mode = lightAvailable&&MODES.includes(appearance)?appearance:preference;
  const automatic = automaticAppearanceAt(now);
  const resolved = mode==='auto'?automatic.theme:mode;
  const daylight = !forceTheme && isDaylightAppearance(location, window.location.origin, resolved);
  const gallery = !forceTheme && isGalleryPreview(location, window.location.origin, resolved);
  const theme = forceTheme || (daylight ? 'light' : 'dark');

  // Update while open and immediately after a sleeping phone/tab resumes.
  // No navigation, server writes, geolocation or OS-theme dependency.
  const automaticActive=lightAvailable&&mode==='auto';
  useLayoutEffect(()=>{if(automaticActive)setNow(new Date());},[automaticActive,pathname]);
  useEffect(()=>{
    if(!automaticActive)return;
    let timer;
    const refresh=()=>setNow(new Date());
    const tick=()=>{refresh();timer=window.setTimeout(tick,60000-Date.now()%60000+25);};
    const visible=()=>{if(document.visibilityState!=='hidden')refresh();};
    timer=window.setTimeout(tick,60000-Date.now()%60000+25);
    window.addEventListener('focus',refresh);document.addEventListener('visibilitychange',visible);
    return()=>{window.clearTimeout(timer);window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',visible);};
  },[automaticActive]);

  // Preview URLs affect this view only. Only a deliberate menu choice persists.

  useLayoutEffect(() => {
    applyTheme(theme);
    const patients = /^\/praxis-tool\/(patients|cases|prescriptions|solved-cases|intake-form|case-questions|account)(\/|$)/.test(pathname);
    document.body.toggleAttribute('data-ea-patients', patients);
    document.body.toggleAttribute('data-ea-gallery', gallery);
    document.body.toggleAttribute('data-ea-daylight', daylight);
    document.documentElement.style.colorScheme = theme;
    const color = daylight ? '#f8f8f5' : patients ? '#04121b' : pathname === "/praxis-tool/learning" ? "#0c1924" : (TOKENS[theme] || TOKENS.dark).bg;
    document.documentElement.style.backgroundColor = color;
    document.body.style.backgroundColor = color;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", color);
    return () => {document.body.removeAttribute('data-ea-patients');document.body.removeAttribute('data-ea-gallery');document.body.removeAttribute('data-ea-daylight');};
  }, [pathname, theme, gallery, daylight]);

  const change = useCallback((next) => {
    if (!lightAvailable || !MODES.includes(next)) return;
    setSaved({owner,value:next});
    try { window.localStorage.setItem(KEY+owner, next); } catch { /* preference is optional */ }
    // Replace only the appearance parameter, preserving remedy, filters and hash.
    const query = new URLSearchParams(location.search);
    query.set('appearance', next);
    navigate({...location, search: `?${query}`}, {replace: true});
  }, [lightAvailable, location, navigate, owner]);
  const toggle = useCallback(() => change(theme === "dark" ? "light" : "dark"), [theme, change]);
  return <ThemeContext.Provider value={{theme, preference:mode, automaticBasis:automatic.basis, lightAvailable, setTheme: change, toggle}}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
